import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useUpdateCompany } from '../useCompanies'
import { CompanyForm } from './CompanyForm'
import type { Company, UpdateCompanyInput } from '../types'

interface EditCompanyDialogProps {
  open: boolean
  onClose: () => void
  company: Company
  onSuccess?: () => void
}

export function EditCompanyDialog({ open, onClose, company, onSuccess }: EditCompanyDialogProps) {
  const { t } = useTranslation()
  const updateCompany = useUpdateCompany()

  async function handleSubmit(values: UpdateCompanyInput) {
    await updateCompany.mutateAsync({ id: company.id, input: values })
    onSuccess?.()
    onClose()
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="bg-background data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] fixed top-1/2 left-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg border shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div>
              <Dialog.Title className="text-sm font-semibold">{t('companies.edit')}</Dialog.Title>
              <Dialog.Description className="text-muted-foreground text-[11px]">
                {company.nameTh}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                className="text-muted-foreground hover:text-foreground rounded p-1 transition-colors"
                aria-label={t('actions.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="px-6 pt-4 pb-6">
            {updateCompany.error && (
              <div className="border-destructive/40 bg-destructive/10 text-destructive mb-4 rounded-lg border px-4 py-2 text-xs">
                {t('companies.errors.updateFailed')}
              </div>
            )}
            <CompanyForm
              mode="edit"
              initialData={company}
              onSubmit={handleSubmit}
              isLoading={updateCompany.isPending}
              onCancel={onClose}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
